import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useAdminContext } from '../../contexts/AdminContext';
import { supabase, Card, codeTypes } from '../../lib/supabase';
import { categories } from '../../mocks/categories';
import CardItem from './components/CardItem';
import { ThemeToggle } from '../../components/ui/theme-toggle';
import { Button } from '../../components/ui/button';
import { GradientText } from '../../components/ui/gradient-text';
import { FilterButton } from '../../components/ui/filter-button';
import { IconButton } from '../../components/ui/icon-button';
import { Skeleton, SkeletonCard } from '../../components/ui/skeleton';
import { UserProfileDropdown } from '../../components/ui/user-profile-dropdown';
import { AnimatedTitle } from '../../components/ui/animated-title';
import { motion, AnimatePresence } from 'motion/react';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerTrigger,
  DrawerClose,
} from '../../components/ui/drawer';

export default function Dashboard() {
  const [cards, setCards] = useState<Card[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedType, setSelectedType] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [allCardsForStats, setAllCardsForStats] = useState<Card[]>([]); // Para estatísticas - carrega tudo
  const [showDuplicatesModal, setShowDuplicatesModal] = useState(false);
  const [duplicates, setDuplicates] = useState<{ [key: string]: Card[] }>({});
  const [removingDuplicates, setRemovingDuplicates] = useState(false);
  const [availableCategories, setAvailableCategories] = useState<string[]>(categories); // Categorias dinâmicas do banco
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Card[]>([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [selectedCardFromSearch, setSelectedCardFromSearch] = useState<string | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [expandedFilterSection, setExpandedFilterSection] = useState<string | null>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const mobileSearchRef = useRef<HTMLDivElement>(null);
  const desktopSearchRef = useRef<HTMLDivElement>(null);
  const { user, loading: authLoading, signOut } = useAuth();
  const { isAdmin, loading: adminLoading } = useAdminContext();
  const navigate = useNavigate();
  const location = useLocation();

  const CARDS_PER_PAGE = 24;

  // Função auxiliar para atualizar duplicatas
  // Detecta duplicados por TÍTULO ou por CÓDIGO (hash)
  const updateDuplicates = useCallback((cardsData: Card[]) => {
    const duplicateGroups: { [key: string]: Card[] } = {};
    const titleCount: { [key: string]: Card[] } = {};
    const hashCount: { [key: string]: Card[] } = {};

    // Agrupar por TÍTULO
    cardsData.forEach(card => {
      if (!titleCount[card.title]) {
        titleCount[card.title] = [];
      }
      titleCount[card.title].push(card);
    });

    // Agrupar por HASH (código idêntico)
    cardsData.forEach(card => {
      // Apenas agrupar se o hash existir e não for vazio
      if (card.code_hash && card.code_hash.trim() !== '') {
        if (!hashCount[card.code_hash]) {
          hashCount[card.code_hash] = [];
        }
        hashCount[card.code_hash].push(card);
      }
    });

    // Adicionar duplicados de TÍTULO
    Object.entries(titleCount).forEach(([title, cards]) => {
      if (cards.length > 1) {
        duplicateGroups[`title:${title}`] = cards.sort((a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
      }
    });

    // Adicionar duplicados de CÓDIGO (hash)
    Object.entries(hashCount).forEach(([hash, cards]) => {
      if (cards.length > 1) {
        // Usar hash + título do primeiro card como chave para evitar conflito
        const key = `code:${hash.substring(0, 8)}:${cards[0].title}`;
        duplicateGroups[key] = cards.sort((a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
      }
    });

    setDuplicates(duplicateGroups);
  }, []);

  // Função para carregar todos os cards para estatísticas (sem paginação)
  // Otimizado: carrega apenas os campos necessários para evitar timeout
  // Cache: só recarrega se necessário
  const fetchAllCardsForStats = useCallback(async (forceRefresh = false) => {
    try {
      // Se já temos dados e não é refresh forçado, não recarregar
      if (!forceRefresh && allCardsForStats.length > 0) {
        console.log('Estatísticas já carregadas, usando cache');
        return;
      }

      console.log('Carregando todos os cards para estatísticas...');

      // Verificar autenticação antes de fazer a query
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) {
        console.error('Erro de autenticação ao carregar estatísticas:', sessionError);
        return;
      }
      console.log('User ID para estatísticas:', session.user.id);

      // Carregar apenas os campos necessários para estatísticas (sem o campo 'data' que pode ser muito grande)
      // Isso reduz drasticamente o tamanho da resposta e evita timeout
      const { data, error } = await supabase
        .from('cards')
        .select('id, title, code_type, category, categories, created_at, code_hash')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Erro ao carregar estatísticas:', error);
        console.error('Detalhes do erro:', JSON.stringify(error, null, 2));
        // Não tentar novamente automaticamente para evitar loops
        // O usuário pode recarregar a página se necessário
      } else {
        console.log(`Cards para estatísticas carregados: ${data?.length || 0}`);
        setAllCardsForStats(data || []);
        // Atualizar duplicatas com todos os cards
        updateDuplicates(data || []);
      }
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  }, [updateDuplicates, allCardsForStats.length]);

  // Função para carregar cards visuais com paginação e filtros
  // Otimizado: evita recarregar se os dados já estão disponíveis
  const fetchCards = useCallback(async (page: number = 0, append: boolean = false, typeFilter?: string, categoryFilter?: string) => {
    try {
      if (page === 0) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      // Verificar autenticação antes de fazer a query
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) {
        console.error('Erro de autenticação:', sessionError);
        alert('Erro de autenticação. Faça login novamente.');
        navigate('/login', { replace: true });
        return;
      }

      console.log(`Carregando cards visuais - página ${page}, tipo: ${typeFilter || 'todos'}, categoria: ${categoryFilter || 'todas'}...`);
      console.log('User ID autenticado:', session.user.id);

      // OTIMIZAÇÃO CRÍTICA: Não carregar o campo 'data' completo (pode ser muito pesado)
      // Carregar apenas campos necessários para exibição na lista
      // O campo 'data' será carregado sob demanda quando o usuário clicar em copiar/visualizar
      let query = supabase
        .from('cards')
        .select('id, user_id, title, thumbnail, category, categories, code_type, is_editable, created_at, code_hash', { count: 'exact' })
        .order('created_at', { ascending: false });

      // Aplicar filtro de tipo se houver
      if (typeFilter) {
        query = query.eq('code_type', typeFilter);
      }

      // Aplicar filtro de categoria se houver
      if (categoryFilter) {
        // Buscar cards onde a categoria está no array categories ou no campo category
        // Usar contains para verificar se o array categories contém a categoria
        // Sintaxe correta: cs.{valor} para arrays
        query = query.or(`category.eq.${categoryFilter},categories.cs.{${categoryFilter}}`);
      }

      // Aplicar paginação
      const { data, error, count } = await query
        .range(page * CARDS_PER_PAGE, (page + 1) * CARDS_PER_PAGE - 1);

      if (error) {
        console.error('Erro do Supabase:', error);
        console.error('Detalhes do erro:', JSON.stringify(error, null, 2));
        alert(`Erro ao carregar dados: ${error.message}`);
      } else {
        console.log(`Cards visuais carregados: ${data?.length || 0} de ${count || 0}`);
        if (data && data.length > 0) {
          console.log('Primeiro card sample:', { id: data[0].id, title: data[0].title, user_id: data[0].user_id });
        }

        if (append) {
          setCards(prev => [...prev, ...(data || [])]);
        } else {
          setCards(data || []);
        }

        // Verificar se há mais cards para carregar
        const totalLoaded = (page + 1) * CARDS_PER_PAGE;
        setHasMore(totalLoaded < (count || 0));
      }
    } catch (error) {
      console.error('Erro de conexão:', error);
      alert('Erro de conexão com o banco de dados. Verifique sua internet e tente novamente.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [navigate]);

  // Função para carregar mais cards
  const loadMoreCards = useCallback(async () => {
    if (loadingMore || !hasMore) return;

    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    await fetchCards(nextPage, true, selectedType, selectedCategory);
  }, [currentPage, loadingMore, hasMore, fetchCards, selectedType, selectedCategory]);

  // Efeito de autenticação otimizado - carrega dados apenas uma vez
  // Usar useRef para evitar múltiplas execuções
  const hasLoadedRef = useRef(false);
  const isRefreshingRef = useRef(false);

  useEffect(() => {
    console.log('Dashboard useEffect - authLoading:', authLoading, 'user:', user ? 'existe' : 'não existe');

    // Aguardar o carregamento da autenticação terminar
    if (authLoading) {
      console.log('Aguardando carregamento da autenticação...');
      return;
    }

    // Verificar sessão diretamente se o user ainda não estiver disponível
    const checkAuth = async () => {
      if (!user) {
        console.log('User não encontrado, verificando sessão...');
        // Verificar sessão diretamente antes de redirecionar
        const { data: { session }, error } = await supabase.auth.getSession();
        console.log('Sessão verificada no dashboard:', session ? 'Sessão encontrada' : 'Sem sessão', error);

        if (!session) {
          console.log('Sem sessão, redirecionando para login...');
          navigate('/login', { replace: true });
          return;
        }
        // Se há sessão mas user ainda não atualizou, aguardar um pouco
        console.log('Sessão encontrada mas user não atualizado, aguardando...');
        await new Promise(resolve => setTimeout(resolve, 500));
        // Verificar novamente após aguardar
        const { data: { session: session2 } } = await supabase.auth.getSession();
        if (!session2) {
          navigate('/login', { replace: true });
          return;
        }
        return;
      }

      // Verificar se há refresh pendente antes de carregar inicialmente
      const hasRefreshPending = (location.state as any)?.refresh === true;

      // Se já carregou e não está fazendo refresh, não fazer nada
      if (hasLoadedRef.current && !isRefreshingRef.current && !hasRefreshPending) {
        return;
      }

      // Se há refresh pendente, deixar o useEffect de refresh lidar com isso
      if (hasRefreshPending) {
        return;
      }

      // Só carregar dados se ainda não foram carregados
      if (!hasLoadedRef.current && user) {
        hasLoadedRef.current = true;
        console.log('User encontrado, carregando dados pela primeira vez...');

        // Carregar todos os cards para estatísticas
        fetchAllCardsForStats(false);

        // Carregar primeira página de cards visuais
        setCurrentPage(0);
        fetchCards(0, false, selectedType, selectedCategory);
      }
    };

    checkAuth();
  }, [user, authLoading, navigate, fetchCards, fetchAllCardsForStats, selectedType, selectedCategory, location.state]);

  // Efeito separado para detectar refresh (vindo do create-card)
  // Este efeito tem prioridade sobre o carregamento inicial
  const refreshProcessedRef = useRef(false);

  useEffect(() => {
    const needsRefresh = (location.state as any)?.refresh === true;

    // Se não há refresh ou já foi processado, não fazer nada
    if (!needsRefresh || refreshProcessedRef.current || isRefreshingRef.current) {
      return;
    }

    // Marcar como processado imediatamente para evitar múltiplas execuções
    refreshProcessedRef.current = true;
    isRefreshingRef.current = true;

    // Limpar o state de refresh imediatamente para evitar múltiplas execuções
    window.history.replaceState({}, document.title);
    console.log('Refresh solicitado, recarregando dados...');

    // Marcar como carregado para evitar carregamento inicial duplicado
    hasLoadedRef.current = true;

    // Carregar todos os cards para estatísticas (forçar refresh)
    fetchAllCardsForStats(true).finally(() => {
      isRefreshingRef.current = false;
      // Resetar o flag após um pequeno delay para permitir novo refresh no futuro
      setTimeout(() => {
        refreshProcessedRef.current = false;
      }, 1000);
    });

    // Carregar primeira página de cards visuais
    setCurrentPage(0);
    fetchCards(0, false, selectedType, selectedCategory);
  }, [location.state, fetchCards, fetchAllCardsForStats, selectedType, selectedCategory]);

  // Recarregar cards quando os filtros mudarem - com debounce para evitar queries duplicadas
  const prevFiltersRef = useRef({ type: selectedType, category: selectedCategory });
  const isInitialMount = useRef(true);

  useEffect(() => {
    // Ignorar a primeira execução (mount inicial)
    if (isInitialMount.current) {
      isInitialMount.current = false;
      prevFiltersRef.current = { type: selectedType, category: selectedCategory };
      return;
    }

    // Só executar se os filtros realmente mudaram e se já temos dados carregados
    const filtersChanged =
      prevFiltersRef.current.type !== selectedType ||
      prevFiltersRef.current.category !== selectedCategory;

    if (user && !authLoading && filtersChanged && hasLoadedRef.current) {
      console.log('Filtros alterados, recarregando cards...', { selectedType, selectedCategory });

      // Limpar filtro de busca quando os filtros mudarem
      setSelectedCardFromSearch(null);

      // Atualizar referência dos filtros anteriores
      prevFiltersRef.current = { type: selectedType, category: selectedCategory };

      // Debounce: aguardar 300ms antes de recarregar para evitar queries duplicadas
      const timeoutId = setTimeout(() => {
        setCurrentPage(0);
        fetchCards(0, false, selectedType, selectedCategory);
      }, 300);

      return () => clearTimeout(timeoutId);
    }
  }, [selectedType, selectedCategory, user, authLoading, fetchCards]);

  // Cards filtrados - se houver um card selecionado da busca, mostrar apenas ele
  const filteredCards = useMemo(() => {
    if (selectedCardFromSearch) {
      return cards.filter(card => card.id === selectedCardFromSearch);
    }
    return cards;
  }, [cards, selectedCardFromSearch]);

  // Memoizar contadores para evitar recálculos (usando allCardsForStats para estatísticas completas)
  const typeCounters = useMemo(() => {
    return {
      json: allCardsForStats.filter(card => card.code_type === 'json').length,
      css: allCardsForStats.filter(card => card.code_type === 'css').length,
      js: allCardsForStats.filter(card => card.code_type === 'js').length,
      html: allCardsForStats.filter(card => card.code_type === 'html').length,
      other: allCardsForStats.filter(card => card.code_type === 'other').length
    };
  }, [allCardsForStats]);

  // Carregar categorias dinamicamente do banco (igual à página de criar card)
  useEffect(() => {
    const loadCategories = async () => {
      try {
        // Começar com a lista padrão do mock
        const categorySet = new Set<string>(categories);

        // Buscar apenas campos de categoria do banco de dados
        const { data, error } = await supabase
          .from('cards')
          .select('categories, category');

        if (!error && data) {
          data.forEach(card => {
            // Adicionar categorias do campo categories (array)
            if (card.categories && Array.isArray(card.categories)) {
              card.categories.forEach(cat => {
                if (cat && cat.trim()) {
                  categorySet.add(cat.trim());
                }
              });
            }
            // Adicionar categoria do campo category (string)
            if (card.category && card.category.trim()) {
              categorySet.add(card.category.trim());
            }
          });
        }

        // Converter para array e ordenar
        const categoriesArray = Array.from(categorySet).sort();
        setAvailableCategories(categoriesArray);
      } catch (error) {
        console.error('Erro ao carregar categorias:', error);
        // Em caso de erro, usar apenas a lista padrão
        setAvailableCategories([...categories].sort());
      }
    };

    if (user) {
      loadCategories();
    }
  }, [user]);

  const categoryCounters = useMemo(() => {
    const counters: { [key: string]: number } = {};
    availableCategories.forEach(category => {
      counters[category] = allCardsForStats.filter(card => {
        const cardCategories = card.categories || [card.category];
        return cardCategories.includes(category);
      }).length;
    });
    return counters;
  }, [allCardsForStats, availableCategories]);

  // Função de busca otimizada - retorna apenas id, title, thumbnail (sem data)
  const performSearch = useCallback(async (query: string) => {
    console.log('Realizando busca:', query);
    if (!query.trim() || query.length < 2) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    setLoadingSearch(true);
    try {
      // Buscar apenas os campos leves (sem data) usando ilike para busca case-insensitive
      const { data, error } = await supabase
        .from('cards')
        .select('id, user_id, title, thumbnail, category, categories, code_type, created_at')
        .ilike('title', `%${query.trim()}%`)
        .limit(10) // Limitar a 10 resultados para performance
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Erro ao buscar cards:', error);
        setSearchResults([]);
      } else {
        setSearchResults(data || []);
        setShowSearchResults(true);
      }
    } catch (error) {
      console.error('Erro inesperado na busca:', error);
      setSearchResults([]);
    } finally {
      setLoadingSearch(false);
    }
  }, []);

  // Debounce para busca - aguarda 300ms após o usuário parar de digitar
  useEffect(() => {
    // Limpar timeout anterior
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Se a query estiver vazia, limpar resultados
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    // Aguardar 300ms antes de buscar
    searchTimeoutRef.current = setTimeout(() => {
      performSearch(searchQuery);
    }, 300);

    // Cleanup
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery, performSearch]);

  // Fechar dropdown quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isOutsideMobile = !mobileSearchRef.current || !mobileSearchRef.current.contains(target);
      const isOutsideDesktop = !desktopSearchRef.current || !desktopSearchRef.current.contains(target);

      if (isOutsideMobile && isOutsideDesktop) {
        // Verificar se o clique não foi no dropdown de resultados
        const targetEl = event.target as HTMLElement;
        if (!targetEl.closest('.search-results-dropdown')) {
          // Debug apenas se for fechar suspeitamente
          // console.log('Fechando busca por clique fora', { isOutsideMobile, isOutsideDesktop });
          setShowSearchResults(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fechar menu mobile com ESC
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isMobileMenuOpen]);

  // Toggle accordion de filtros
  const toggleFilterSection = (section: string) => {
    setExpandedFilterSection(expandedFilterSection === section ? null : section);
  };

  const removeDuplicates = useCallback(async () => {
    setRemovingDuplicates(true);
    try {
      const idsToDelete: string[] = [];

      Object.values(duplicates).forEach(duplicateGroup => {
        if (duplicateGroup.length > 1) {
          const toDelete = duplicateGroup.slice(1);
          idsToDelete.push(...toDelete.map(card => card.id));
        }
      });

      if (idsToDelete.length > 0) {
        const { error } = await supabase
          .from('cards')
          .delete()
          .in('id', idsToDelete);

        if (error) {
          console.error('Erro ao remover duplicatas:', error);
          alert('Erro ao remover duplicatas. Tente novamente.');
        } else {
          alert(`${idsToDelete.length} itens duplicados removidos com sucesso!`);
          setShowDuplicatesModal(false);
          // Atualização otimista: remover duplicatas do estado local
          setCards(prev => prev.filter(card => !idsToDelete.includes(card.id)));
          setAllCardsForStats(prev => {
            const updated = prev.filter(card => !idsToDelete.includes(card.id));
            updateDuplicates(updated);
            return updated;
          });
        }
      }
    } catch (error) {
      console.error('Erro inesperado:', error);
      alert('Erro inesperado ao remover duplicatas.');
    } finally {
      setRemovingDuplicates(false);
    }
  }, [duplicates, fetchCards]);

  const getDuplicateCount = useCallback(() => {
    return Object.values(duplicates).reduce((total, group) => total + (group.length - 1), 0);
  }, [duplicates]);

  const handleLogout = useCallback(async () => {
    await signOut();
    navigate('/login', { replace: true });
  }, [signOut, navigate]);

  const handleDeleteCard = useCallback((deletedCardId: string) => {
    // Atualização otimista: remover o card do estado local imediatamente
    setCards(prev => prev.filter(card => card.id !== deletedCardId));
    setAllCardsForStats(prev => {
      const updated = prev.filter(card => card.id !== deletedCardId);
      updateDuplicates(updated);
      return updated;
    });
  }, [updateDuplicates]);

  // Memoizar filtros de tipo
  const typeFilters = useMemo(() => [
    {
      type: 'json',
      label: 'JSON',
      icon: 'ri-braces-line',
      count: typeCounters.json
    },
    {
      type: 'css',
      label: 'CSS',
      icon: 'ri-css3-line',
      count: typeCounters.css
    },
    {
      type: 'js',
      label: 'JavaScript',
      icon: 'ri-javascript-line',
      count: typeCounters.js
    },
    {
      type: 'html',
      label: 'HTML',
      icon: 'ri-html5-line',
      count: typeCounters.html
    },
    {
      type: 'other',
      label: 'Outros',
      icon: 'ri-code-line',
      count: typeCounters.other
    }
  ], [typeCounters]);

  // Memoizar filtros de categoria - usando categorias dinâmicas do banco
  const categoryFilters = useMemo(() =>
    availableCategories.map(category => ({
      id: category,
      name: category,
      count: categoryCounters[category] || 0
    }))
    , [categoryCounters, availableCategories]);

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gradient-to-br dark:from-[#1a0f2b] dark:via-[#2d1b3d] dark:to-[#1a0f2b] transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Skeleton Header */}
          <div className="mb-8">
            <Skeleton variant="text" width="200px" height="32px" className="mb-2" />
            <Skeleton variant="text" width="150px" height="20px" />
          </div>

          {/* Skeleton Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="glass-card rounded-xl shadow-lg p-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <Skeleton variant="text" width="60%" height="16px" className="mb-2" />
                    <Skeleton variant="text" width="40%" height="24px" />
                  </div>
                  <Skeleton variant="circular" width={48} height={48} />
                </div>
              </div>
            ))}
          </div>

          {/* Skeleton Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gradient-to-br dark:from-[#1a0f2b] dark:via-[#2d1b3d] dark:to-[#1a0f2b] transition-colors duration-300">
      {/* Header */}
      {/* Mobile Menu Drawer */}
      <Drawer open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen} direction="left">
        <header className="bg-white dark:bg-gradient-to-r dark:from-[#1a0f2b] dark:to-[#0d0d0d] shadow-lg border-b border-gray-200 dark:border-[#8a2be2]/30">
          <div className="w-full px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16 w-full">
              {/* Lado Esquerdo - Título */}
              <div className="flex items-center space-x-2 sm:space-x-3">
                {/* Botão Hambúrguer - Mobile */}
                <DrawerTrigger asChild>
                  <IconButton
                    variant="ghost"
                    size="md"
                    className="lg:hidden flex-shrink-0"
                    aria-label="Abrir menu"
                  >
                    <i className="ri-menu-line text-xl"></i>
                  </IconButton>
                </DrawerTrigger>

                <div className="overflow-hidden">
                  <AnimatedTitle className="hidden sm:block" />
                  <AnimatedTitle className="sm:hidden text-xs" />
                </div>
              </div>

              {/* Lado Direito - Ações */}
              <div className="flex items-center space-x-2 lg:space-x-4">
                {getDuplicateCount() > 0 && (
                  <Button
                    onClick={() => setShowDuplicatesModal(true)}
                    type="primary"
                    size="small"
                    className="hidden lg:flex items-center relative"
                    prefix={<i className="ri-delete-bin-2-line"></i>}
                  >
                    Remover Duplicatas
                    <span className="absolute -top-2 -right-2 bg-white text-[#0d1b2a] text-xs rounded-full w-6 h-6 flex items-center justify-center font-bold shadow-lg">
                      {getDuplicateCount()}
                    </span>
                  </Button>
                )}
                <ThemeToggle />
                <UserProfileDropdown />
              </div>
            </div>
          </div>
        </header>

        <DrawerContent side="left" className="lg:hidden">
          <DrawerHeader className="flex-row items-center justify-between space-y-0 pb-4">
            <div className="flex items-center min-w-0 flex-1">
              <AnimatedTitle className="text-xs sm:text-base" />
            </div>
          </DrawerHeader>

          <DrawerBody className="flex flex-col p-0">
            {/* Botões de Ação */}
            <div className="p-4 border-b border-gray-200 dark:border-white/10 space-y-2">
              <Button
                onClick={() => {
                  navigate('/create-card');
                  setIsMobileMenuOpen(false);
                }}
                type="primary"
                size="large"
                fullWidth
                prefix={<i className="ri-add-line"></i>}
              >
                Novo Código
              </Button>
              {getDuplicateCount() > 0 && (
                <Button
                  onClick={() => {
                    setShowDuplicatesModal(true);
                    setIsMobileMenuOpen(false);
                  }}
                  type="primary"
                  size="large"
                  fullWidth
                  className="relative"
                  prefix={<i className="ri-delete-bin-2-line"></i>}
                >
                  Remover Duplicatas
                  <span className="absolute -top-2 -right-2 bg-white text-[#0d1b2a] text-xs rounded-full w-6 h-6 flex items-center justify-center font-bold shadow-lg">
                    {getDuplicateCount()}
                  </span>
                </Button>
              )}
            </div>

            {/* Filtros em Accordion */}
            <div className="flex-1 overflow-y-auto p-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-[#e0e1dd] mb-4 font-ubuntu">Filtros</h3>

              {/* Campo de Pesquisa no Mobile */}
              <div className="mb-4 relative z-50" ref={mobileSearchRef}>
                <div className="relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      if (e.target.value.trim().length >= 2) {
                        setShowSearchResults(true);
                      }
                    }}
                    onFocus={() => {
                      if (searchResults.length > 0) {
                        setShowSearchResults(true);
                      }
                    }}
                    placeholder="pesquisar card"
                    className="w-full px-4 py-3 pl-12 pr-12 border border-gray-300 dark:border-[#8a2be2]/30 rounded-lg focus:ring-2 focus:ring-[#8a2be2] focus:border-[#8a2be2] dark:bg-[#0d0d0d]/50 dark:text-[#e0e1dd] transition-colors duration-200 font-montserrat shadow-sm"
                  />
                  <i className="ri-search-line absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-[#c780ff] text-lg"></i>
                  {loadingSearch && (
                    <i className="ri-loader-4-line absolute right-4 top-1/2 transform -translate-y-1/2 text-[#6a0dad] dark:text-[#c780ff] animate-spin"></i>
                  )}
                  {searchQuery && !loadingSearch && (
                    <button
                      onClick={() => {
                        setSearchQuery('');
                        setSearchResults([]);
                        setShowSearchResults(false);
                        setSelectedCardFromSearch(null);
                      }}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-[#c780ff] hover:text-gray-600 dark:hover:text-[#e0e1dd]"
                    >
                      <i className="ri-close-line text-lg"></i>
                    </button>
                  )}
                </div>

                {/* Dropdown de Resultados no Mobile */}
                {showSearchResults && searchQuery.trim().length >= 2 && (
                  <div className="search-results-dropdown mt-2 bg-white dark:bg-gradient-to-br dark:from-[#1a0f2b] dark:to-[#2d1b3d] rounded-lg shadow-xl border border-gray-200 dark:border-[#8a2be2]/30 z-50 max-h-64 overflow-y-auto">
                    {loadingSearch ? (
                      <div className="p-4 text-center">
                        <i className="ri-loader-4-line text-2xl text-[#6a0dad] dark:text-[#c780ff] animate-spin"></i>
                        <p className="mt-2 text-sm text-gray-600 dark:text-[#c780ff]">Buscando...</p>
                      </div>
                    ) : searchResults.length > 0 ? (
                      <div className="p-2">
                        {searchResults.map((card) => (
                          <button
                            key={card.id}
                            onClick={() => {
                              setSearchQuery('');
                              setShowSearchResults(false);
                              setSelectedCategory('');
                              setSelectedType('');
                              const cardExists = cards.some(c => c.id === card.id);
                              if (!cardExists) {
                                setCards(prev => [card, ...prev]);
                              }
                              setSelectedCardFromSearch(card.id);
                              setIsMobileMenuOpen(false);
                              setTimeout(() => {
                                const cardElement = document.querySelector(`[data-card-id="${card.id}"]`);
                                if (cardElement) {
                                  cardElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                  cardElement.classList.add('ring-4', 'ring-[#8a2be2]', 'ring-opacity-50', 'transition-all', 'duration-300');
                                  setTimeout(() => {
                                    cardElement.classList.remove('ring-4', 'ring-[#8a2be2]', 'ring-opacity-50');
                                  }, 2000);
                                }
                              }, 100);
                            }}
                            className="w-full flex items-center space-x-3 p-3 hover:bg-gray-50 dark:hover:bg-white/10 rounded-lg transition-colors duration-200 text-left"
                          >
                            {card.thumbnail ? (
                              <img
                                src={card.thumbnail}
                                alt={card.title}
                                className="w-12 h-12 object-cover rounded-lg border border-gray-200 dark:border-[#8a2be2]/30 flex-shrink-0"
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                }}
                              />
                            ) : (
                              <div className="w-12 h-12 bg-gray-100 dark:bg-[#0d0d0d] rounded-lg border border-gray-200 dark:border-[#8a2be2]/30 flex items-center justify-center flex-shrink-0">
                                <i className="ri-image-line text-xl text-gray-400 dark:text-[#c780ff]"></i>
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 dark:text-[#e0e1dd] truncate font-ubuntu">
                                {card.title}
                              </p>
                              {card.category && (
                                <p className="text-xs text-gray-500 dark:text-[#c780ff] mt-1 font-montserrat">
                                  {card.category}
                                </p>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="p-4 text-center">
                        <i className="ri-search-line text-2xl text-gray-400 dark:text-[#c780ff] mb-2"></i>
                        <p className="text-sm text-gray-600 dark:text-[#c780ff] font-montserrat">
                          Nenhum resultado encontrado
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Tipo de Código - Accordion */}
              <div className="mb-4">
                <button
                  onClick={() => toggleFilterSection('type')}
                  className="w-full flex items-center justify-between p-3 bg-gray-50 dark:bg-[#0d0d0d]/50 rounded-lg hover:bg-gray-100 dark:hover:bg-[#1b263b]/50 transition-colors"
                >
                  <span className="text-sm font-medium text-gray-700 dark:text-[#e0e1dd] font-montserrat">Tipo de Código</span>
                  <i className={`ri-arrow-${expandedFilterSection === 'type' ? 'up' : 'down'}-s-line text-gray-600 dark:text-[#c780ff] transition-transform`}></i>
                </button>

                {expandedFilterSection === 'type' && (
                  <div className="mt-2 space-y-2">
                    {typeFilters.map((filter) => (
                      <FilterButton
                        key={filter.type}
                        onClick={() => {
                          setSelectedType(selectedType === filter.type ? '' : filter.type);
                          setSelectedCardFromSearch(null);
                        }}
                        active={selectedType === filter.type}
                        count={filter.count}
                        className="w-full justify-between text-sm"
                      >
                        <div className="flex items-center space-x-2">
                          <i className={filter.icon}></i>
                          <span>{filter.label}</span>
                        </div>
                      </FilterButton>
                    ))}
                  </div>
                )}
              </div>

              {/* Categorias - Accordion */}
              <div>
                <button
                  onClick={() => toggleFilterSection('category')}
                  className="w-full flex items-center justify-between p-3 bg-gray-50 dark:bg-[#0d0d0d]/50 rounded-lg hover:bg-gray-100 dark:hover:bg-[#1b263b]/50 transition-colors"
                >
                  <span className="text-sm font-medium text-gray-700 dark:text-[#e0e1dd] font-montserrat">Categorias</span>
                  <i className={`ri-arrow-${expandedFilterSection === 'category' ? 'up' : 'down'}-s-line text-gray-600 dark:text-[#c780ff] transition-transform`}></i>
                </button>

                {expandedFilterSection === 'category' && (
                  <div className="mt-2 space-y-2">
                    {categoryFilters.map((filter) => (
                      <FilterButton
                        key={filter.id}
                        onClick={() => {
                          setSelectedCategory(selectedCategory === filter.id ? '' : filter.id);
                          setSelectedCardFromSearch(null);
                        }}
                        active={selectedCategory === filter.id}
                        count={filter.count}
                        className="w-full justify-between"
                      >
                        <span>{filter.name}</span>
                      </FilterButton>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      <main className="flex flex-col lg:flex-row">
        {/* Sidebar de Filtros - Desktop Only */}
        <aside className="hidden lg:block w-72 flex-shrink-0 bg-[#1a0f2b]/80 backdrop-blur-xl min-h-screen sticky top-0 border-r border-white/10 z-10">
          <div className="p-6">
            <h3 className="text-xl font-bold text-white mb-6 font-ubuntu flex items-center space-x-2">
              <i className="ri-filter-line"></i>
              <span>Filtros</span>
            </h3>

            {/* Campo de Pesquisa */}
            <div className="mb-6 relative z-50" ref={desktopSearchRef}>
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    if (e.target.value.trim().length >= 2) {
                      setShowSearchResults(true);
                    }
                  }}
                  onFocus={() => {
                    if (searchResults.length > 0) {
                      setShowSearchResults(true);
                    }
                  }}
                  placeholder="pesquisar card"
                  className="w-full px-4 py-3 pl-12 pr-12 border border-white/20 dark:border-white/30 rounded-lg focus:ring-2 focus:ring-white/50 focus:border-white/50 bg-white/10 dark:bg-white/5 backdrop-blur-sm text-white placeholder-white/70 transition-colors duration-200 font-montserrat shadow-sm"
                />
                <i className="ri-search-line absolute left-4 top-1/2 transform -translate-y-1/2 text-white/70 text-lg"></i>
                {loadingSearch && (
                  <i className="ri-loader-4-line absolute right-4 top-1/2 transform -translate-y-1/2 text-white/70 animate-spin"></i>
                )}
                {searchQuery && !loadingSearch && (
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      setSearchResults([]);
                      setShowSearchResults(false);
                      setSelectedCardFromSearch(null);
                    }}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white/70 hover:text-white"
                  >
                    <i className="ri-close-line text-lg"></i>
                  </button>
                )}
              </div>

              {/* Dropdown de Resultados */}
              {showSearchResults && searchQuery.trim().length >= 2 && (
                <div className="search-results-dropdown absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gradient-to-br dark:from-[#1a0f2b] dark:to-[#2d1b3d] rounded-lg shadow-2xl border border-gray-200 dark:border-[#8a2be2]/30 z-50 max-h-96 overflow-y-auto">
                  {loadingSearch ? (
                    <div className="p-4 text-center">
                      <i className="ri-loader-4-line text-2xl text-[#6a0dad] dark:text-[#c780ff] animate-spin"></i>
                      <p className="mt-2 text-sm text-gray-600 dark:text-[#c780ff]">Buscando...</p>
                    </div>
                  ) : searchResults.length > 0 ? (
                    <div className="p-2">
                      {searchResults.map((card) => (
                        <button
                          key={card.id}
                          onClick={() => {
                            // Limpar busca
                            setSearchQuery('');
                            setShowSearchResults(false);

                            // Limpar filtros de categoria e tipo
                            setSelectedCategory('');
                            setSelectedType('');

                            // Verificar se o card já está na lista
                            const cardExists = cards.some(c => c.id === card.id);

                            // Se o card não estiver na lista, adicionar ele
                            if (!cardExists) {
                              setCards(prev => [card, ...prev]);
                            }

                            // Filtrar para mostrar apenas o card selecionado
                            setSelectedCardFromSearch(card.id);

                            // Scroll até o card após um pequeno delay para garantir que o DOM foi atualizado
                            setTimeout(() => {
                              const cardElement = document.querySelector(`[data-card-id="${card.id}"]`);
                              if (cardElement) {
                                cardElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                cardElement.classList.add('ring-4', 'ring-[#8a2be2]', 'ring-opacity-50', 'transition-all', 'duration-300');
                                setTimeout(() => {
                                  cardElement.classList.remove('ring-4', 'ring-[#8a2be2]', 'ring-opacity-50');
                                }, 3000);
                              }
                            }, 100);
                          }}
                          className="w-full text-left p-3 hover:bg-gray-100 dark:hover:bg-white/10 rounded-lg transition-colors"
                        >
                          <div className="flex items-center space-x-3">
                            {card.thumbnail ? (
                              <img
                                src={card.thumbnail}
                                alt={card.title}
                                className="w-12 h-12 object-cover rounded-lg border border-gray-200 dark:border-[#8a2be2]/30 flex-shrink-0"
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.style.display = 'none';
                                  const parent = target.parentElement;
                                  if (parent) {
                                    const fallback = parent.querySelector('.search-result-fallback');
                                    if (fallback) {
                                      (fallback as HTMLElement).style.display = 'flex';
                                    }
                                  }
                                }}
                              />
                            ) : (
                              <div className="w-12 h-12 bg-gray-100 dark:bg-[#0d0d0d] rounded-lg border border-gray-200 dark:border-[#8a2be2]/30 flex items-center justify-center flex-shrink-0">
                                <i className="ri-image-line text-xl text-gray-400 dark:text-[#c780ff]"></i>
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 dark:text-white truncate font-ubuntu">
                                {card.title}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-[#c780ff] mt-1 font-montserrat">
                                {card.code_type?.toUpperCase() || 'N/A'}
                              </p>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="p-4 text-center">
                      <i className="ri-search-line text-2xl text-gray-400 dark:text-[#c780ff] mb-2"></i>
                      <p className="text-sm text-gray-600 dark:text-[#c780ff] font-montserrat">
                        Nenhum resultado encontrado
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Type Filters */}
            <div className="mb-8">
              <h4 className="text-sm font-semibold text-white/90 mb-4 font-montserrat uppercase tracking-wide">Tipo de Código</h4>
              <div className="space-y-2">
                {typeFilters.map((filter) => (
                  <FilterButton
                    key={filter.type}
                    onClick={() => setSelectedType(selectedType === filter.type ? '' : filter.type)}
                    active={selectedType === filter.type}
                    count={filter.count}
                    className="w-full justify-between text-left"
                  >
                    <div className="flex items-center space-x-2">
                      <i className={filter.icon}></i>
                      <span>{filter.label}</span>
                    </div>
                  </FilterButton>
                ))}
              </div>
            </div>

            {/* Category Filters */}
            <div>
              <h4 className="text-sm font-semibold text-white/90 mb-4 font-montserrat uppercase tracking-wide">Categorias</h4>
              <div className="space-y-2 pr-2">
                {categoryFilters.map((filter) => (
                  <FilterButton
                    key={filter.id}
                    onClick={() => setSelectedCategory(selectedCategory === filter.id ? '' : filter.id)}
                    active={selectedCategory === filter.id}
                    count={filter.count}
                    className="w-full justify-between text-left"
                  >
                    <span>{filter.name}</span>
                  </FilterButton>
                ))}
              </div>
            </div>
          </div>
        </aside>

        {/* Conteúdo Principal */}
        <div className="flex-1 min-w-0 lg:max-w-[calc(100%-18rem)]">
          <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
            {/* Stats Cards - Reposicionados e Discretos */}
            <div className="grid grid-cols-4 gap-3 mb-6">
              <div className="glass-card rounded-lg shadow-sm p-3 opacity-80 hover:opacity-100 transition-opacity">
                {/* Desktop Layout */}
                <div className="hidden sm:flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 font-montserrat">Total</p>
                    <p className="text-lg font-bold text-gray-900 dark:text-white font-ubuntu">{allCardsForStats.length}</p>
                  </div>
                  <div className="p-2 bg-[#6a0dad]/20 dark:bg-[#6a0dad]/30 rounded-lg">
                    <i className="ri-file-code-line text-sm text-[#6a0dad] dark:text-[#c780ff]"></i>
                  </div>
                </div>
                {/* Mobile Layout - Título acima, ícone na mesma linha da quantidade */}
                <div className="sm:hidden">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 font-montserrat mb-1">Total</p>
                  <div className="flex items-center space-x-2">
                    <p className="text-base font-bold text-gray-900 dark:text-white font-ubuntu">{allCardsForStats.length}</p>
                    <i className="ri-file-code-line text-base text-[#6a0dad] dark:text-[#c780ff]"></i>
                  </div>
                </div>
              </div>

              <div className="glass-card rounded-lg shadow-sm p-3 opacity-80 hover:opacity-100 transition-opacity">
                {/* Desktop Layout */}
                <div className="hidden sm:flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 font-montserrat">CSS</p>
                    <p className="text-lg font-bold text-gray-900 dark:text-white font-ubuntu">{typeCounters.css}</p>
                  </div>
                  <div className="p-2 bg-[#6a0dad]/20 dark:bg-[#6a0dad]/30 rounded-lg">
                    <i className="ri-css3-line text-sm text-[#6a0dad] dark:text-[#c780ff]"></i>
                  </div>
                </div>
                {/* Mobile Layout - Título acima, ícone na mesma linha da quantidade */}
                <div className="sm:hidden">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 font-montserrat mb-1">CSS</p>
                  <div className="flex items-center space-x-2">
                    <p className="text-base font-bold text-gray-900 dark:text-white font-ubuntu">{typeCounters.css}</p>
                    <i className="ri-css3-line text-base text-[#6a0dad] dark:text-[#c780ff]"></i>
                  </div>
                </div>
              </div>

              <div className="glass-card rounded-lg shadow-sm p-3 opacity-80 hover:opacity-100 transition-opacity">
                {/* Desktop Layout */}
                <div className="hidden sm:flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 font-montserrat">JavaScript</p>
                    <p className="text-lg font-bold text-gray-900 dark:text-white font-ubuntu">{typeCounters.js}</p>
                  </div>
                  <div className="p-2 bg-[#6a0dad]/20 dark:bg-[#6a0dad]/30 rounded-lg">
                    <i className="ri-javascript-line text-sm text-[#6a0dad] dark:text-[#c780ff]"></i>
                  </div>
                </div>
                {/* Mobile Layout - Título acima, ícone na mesma linha da quantidade */}
                <div className="sm:hidden">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 font-montserrat mb-1">JavaScript</p>
                  <div className="flex items-center space-x-2">
                    <p className="text-base font-bold text-gray-900 dark:text-white font-ubuntu">{typeCounters.js}</p>
                    <i className="ri-javascript-line text-base text-[#6a0dad] dark:text-[#c780ff]"></i>
                  </div>
                </div>
              </div>

              <div className="glass-card rounded-lg shadow-sm p-3 opacity-80 hover:opacity-100 transition-opacity">
                {/* Desktop Layout */}
                <div className="hidden sm:flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 font-montserrat">JSON</p>
                    <p className="text-lg font-bold text-gray-900 dark:text-white font-ubuntu">{typeCounters.json}</p>
                  </div>
                  <div className="p-2 bg-[#6a0dad]/20 dark:bg-[#6a0dad]/30 rounded-lg">
                    <i className="ri-braces-line text-sm text-[#6a0dad] dark:text-[#c780ff]"></i>
                  </div>
                </div>
                {/* Mobile Layout - Título acima, ícone na mesma linha da quantidade */}
                <div className="sm:hidden">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 font-montserrat mb-1">JSON</p>
                  <div className="flex items-center space-x-2">
                    <p className="text-base font-bold text-gray-900 dark:text-white font-ubuntu">{typeCounters.json}</p>
                    <i className="ri-braces-line text-base text-[#6a0dad] dark:text-[#c780ff]"></i>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions Bar */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white font-ubuntu">Meus Códigos</h2>
              <Button
                buttonType="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  navigate('/create-card');
                }}
                type="primary"
                size="large"
                className="hidden lg:flex items-center shadow-lg hover:shadow-xl relative z-10"
                prefix={<i className="ri-add-line text-lg"></i>}
              >
                Novo Código
              </Button>
            </div>

            {/* Cards Grid */}
            {filteredCards.length === 0 ? (
              <div className="text-center py-12">
                <i className="ri-file-code-line text-6xl text-gray-400 dark:text-gray-500 mb-4"></i>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2 font-ubuntu">Nenhum código encontrado</h3>
                <p className="text-gray-600 dark:text-gray-300 mb-6 font-montserrat">
                  {allCardsForStats.length === 0
                    ? 'Comece criando seu primeiro código para acelerar seus projetos WordPress.'
                    : 'Tente ajustar os filtros para encontrar o que procura.'
                  }
                </p>
                <Button
                  buttonType="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    navigate('/create-card');
                  }}
                  type="primary"
                  size="large"
                  className="inline-flex items-center space-x-2 bg-gradient-to-r from-[#6a0dad] to-[#8a2be2] text-white shadow-lg hover:shadow-xl hover:shadow-[#8a2be2]/30 relative z-10"
                  prefix={<i className="ri-add-line"></i>}
                >
                  Criar Primeiro Código
                </Button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 auto-rows-fr">
                  {filteredCards.map((card) => (
                    <CardItem
                      key={card.id}
                      card={card}
                      onCardDeleted={handleDeleteCard}
                      onCardUpdated={(updatedCard) => {
                        // Atualização otimista: atualizar o card no estado local
                        setCards(prev => prev.map(c => c.id === updatedCard.id ? updatedCard : c));
                        setAllCardsForStats(prev => {
                          const updated = prev.map(c => c.id === updatedCard.id ? updatedCard : c);
                          updateDuplicates(updated);
                          return updated;
                        });
                      }}
                    />
                  ))}
                </div>

                {/* Botão Carregar Mais */}
                {hasMore && (
                  <div className="flex justify-center mt-8">
                    <Button
                      onClick={loadMoreCards}
                      disabled={loadingMore}
                      loading={loadingMore}
                      type="primary"
                      size="large"
                      className="shadow-lg hover:shadow-xl"
                      prefix={!loadingMore ? <i className="ri-add-line"></i> : undefined}
                    >
                      {loadingMore ? 'Carregando...' : 'Carregar Mais'}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </main>

      {/* Modal de Duplicatas */}
      {showDuplicatesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gradient-to-br dark:from-[#1a0f2b] dark:to-[#2d1b3d] rounded-xl shadow-2xl max-w-4xl w-full max-h-[80vh] overflow-hidden border border-gray-200 dark:border-[#8a2be2]/30">
            <div className="p-6 border-b border-gray-200 dark:border-[#8a2be2]/30">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white font-ubuntu">
                  Itens Duplicados Encontrados
                </h2>
                <IconButton
                  onClick={() => setShowDuplicatesModal(false)}
                  variant="ghost"
                  size="md"
                >
                  <i className="ri-close-line text-xl"></i>
                </IconButton>
              </div>
              <p className="text-gray-600 dark:text-gray-400 mt-2 font-montserrat">
                Encontramos {getDuplicateCount()} itens duplicados. O sistema manterá apenas a versão mais recente de cada item.
              </p>
            </div>

            <div className="p-6 overflow-y-auto max-h-96">
              <div className="space-y-6">
                {Object.entries(duplicates).map(([key, duplicateGroup]) => {
                  // Detectar tipo de duplicação
                  const isDuplicateByCode = key.startsWith('code:');
                  const isDuplicateByTitle = key.startsWith('title:');
                  const displayTitle = isDuplicateByTitle
                    ? key.replace(/^title:/, '')
                    : isDuplicateByCode
                      ? key.replace(/^code:[^:]+:/, '')
                      : key;

                  return (
                    <div key={key} className="border border-gray-200 dark:border-[#8a2be2]/30 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-3 flex-wrap">
                        {isDuplicateByCode && (
                          <span className="px-2 py-1 bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300 text-xs font-semibold rounded font-montserrat">
                            CÓDIGO IDÊNTICO
                          </span>
                        )}
                        {isDuplicateByTitle && (
                          <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 text-xs font-semibold rounded font-montserrat">
                            TÍTULO DUPLICADO
                          </span>
                        )}
                        <h3 className="font-semibold text-gray-900 dark:text-white font-ubuntu">
                          {displayTitle} ({duplicateGroup.length} cópias)
                        </h3>
                      </div>
                      <div className="space-y-2">
                        {duplicateGroup.map((card, index) => (
                          <div
                            key={card.id}
                            className={`flex items-center justify-between p-3 rounded-lg ${index === 0
                                ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                                : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                              }`}
                          >
                            <div className="flex items-center space-x-3">
                              <i className={`${index === 0 ? 'ri-check-line text-green-600' : 'ri-delete-bin-line text-red-600'
                                }`}></i>
                              <div>
                                <p className="font-medium text-gray-900 dark:text-white font-montserrat">
                                  {index === 0 ? 'Manter (mais recente)' : 'Remover'}
                                </p>
                                <p className="text-sm text-gray-600 dark:text-gray-400 font-montserrat">
                                  Criado em: {new Date(card.created_at).toLocaleString('pt-BR')}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <span className="text-sm text-gray-600 dark:text-gray-400 font-montserrat">
                                {card.code_type?.toUpperCase()}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-[#8a2be2]/30 flex justify-end space-x-4">
              <Button
                onClick={() => setShowDuplicatesModal(false)}
                type="secondary"
                size="medium"
              >
                Cancelar
              </Button>
              <Button
                onClick={removeDuplicates}
                disabled={removingDuplicates}
                loading={removingDuplicates}
                type="primary"
                size="medium"
                className="bg-gradient-to-r from-[#6a0dad] to-[#8a2be2] hover:from-[#8a2be2] hover:to-[#c780ff] text-white"
                prefix={!removingDuplicates ? <i className="ri-delete-bin-2-line"></i> : undefined}
              >
                {removingDuplicates ? 'Removendo...' : `Remover ${getDuplicateCount()} Duplicatas`}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
